import { motion } from "framer-motion";
import { Zap, Ruler, ShieldCheck, Battery } from "lucide-react";

/**
 * EquipmentFleet — showcase our professional equipment.
 * Each unit is a "spec card" with hero photo + tactical data.
 * Photos live in /public/brand/equipment/. Add more by appending to
 * EQUIPMENT below.
 *
 * Design philosophy: read like a Porsche Design or Rimowa gear page —
 * clean spec labels, no marketing fluff, quiet confidence.
 */
const EQUIPMENT = [
  {
    key: "sweeper-ride-on",
    name: "Ride-On Parking-Lot Sweeper",
    tagline: "Silent, electric, dawn-shift ready.",
    photo: "/brand/equipment/ride-on-sweeper.webp",
    body: "Fully-enclosed cab, twin rotary side-brooms, and a lithium drivetrain. We deploy it at 5 AM for parking-lot and drive-aisle sweeps — quiet enough for hotels and medical properties, wide enough to clear a full acre in a single pass.",
    specs: [
      { Icon: Ruler,       label: "Sweep width",  value: "72\"" },
      { Icon: Battery,     label: "Range",        value: "8 hr" },
      { Icon: Zap,         label: "Powertrain",   value: "Electric · 0 emissions" },
      { Icon: ShieldCheck, label: "Noise level",  value: "< 70 dB" },
    ],
    badge: "Fleet · Ops",
    year: "In service",
  },
  {
    key: "boom-lift-z45",
    name: "Genie Z-45/25 Articulating Boom Lift",
    tagline: "Up. Over. In. No ladder ever.",
    photo: "/brand/equipment/genie-boom-lift.webp",
    body: "45-foot vertical reach with a 25-foot horizontal outreach — we position over hedges, second-story balconies, and awnings without setting foot on the property. Deployed for exterior pressure-wash, permanent lighting service, gutter clearing, and hard-to-reach facade work.",
    specs: [
      { Icon: Ruler,       label: "Vertical reach",   value: "45 ft" },
      { Icon: Ruler,       label: "Horizontal reach", value: "25 ft" },
      { Icon: ShieldCheck, label: "Rated capacity",   value: "500 lb" },
      { Icon: Zap,         label: "Powertrain",       value: "Electric · Indoor-rated" },
    ],
    badge: "Fleet · At-height",
    year: "In service",
  },
];

export default function EquipmentFleet() {
  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: "var(--bg-2)" }}
      data-testid="equipment-fleet"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(1000px 500px at 80% 20%, rgba(198,168,124,0.08), transparent 60%)",
      }} />
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-3 overline pt-2">The Fleet</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              We show up{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>with the right tool.</span>
            </h2>
            <p className="mt-6 font-body max-w-2xl" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.65 }}>
              We invest in commercial-grade equipment so your property gets treated like the asset it is — not sprayed down with a consumer wand from the hardware store. Here's what rolls up to your site.
            </p>
          </div>
        </motion.div>

        <div className="grid gap-6">
          {EQUIPMENT.map((eq, i) => (
            <EquipmentCard key={eq.key} eq={eq} index={i} />
          ))}
        </div>

        <div className="mt-14 text-center font-mono text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--text-3)" }}>
          Additional units in the fleet · Portfolio manager may request a full equipment list
        </div>
      </div>
    </section>
  );
}

function EquipmentCard({ eq, index }) {
  const isEven = index % 2 === 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="glass-card glass-reflect float-lift overflow-hidden"
      data-testid={`equipment-${eq.key}`}
    >
      <div className={`grid lg:grid-cols-12 gap-0 items-stretch`}>
        {/* Photo */}
        <div
          className={`lg:col-span-7 relative ${isEven ? "" : "lg:order-2"}`}
          style={{ minHeight: 460 }}
        >
          <img
            src={eq.photo}
            alt={eq.name}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "brightness(0.88) contrast(1.05)" }}
            loading="lazy"
          />
          {/* Grade overlay */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: "linear-gradient(90deg, rgba(0,0,0,0) 40%, rgba(10,10,13,0.6) 100%)",
          }} />
          {/* HUD frame */}
          {["tl", "tr", "bl", "br"].map((c) => {
            const isTop = c.startsWith("t"), isLeft = c.endsWith("l");
            return (
              <div key={c} className="absolute pointer-events-none" style={{
                [isTop ? "top" : "bottom"]: "16px",
                [isLeft ? "left" : "right"]: "16px",
                width: "22px", height: "22px",
                borderTop: isTop ? "1px solid var(--gold)" : "none",
                borderBottom: !isTop ? "1px solid var(--gold)" : "none",
                borderLeft: isLeft ? "1px solid var(--gold)" : "none",
                borderRight: !isLeft ? "1px solid var(--gold)" : "none",
                opacity: 0.6,
              }} />
            );
          })}
          {/* Top badge */}
          <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5" style={{
            background: "rgba(0,0,0,0.55)",
            border: "1px solid var(--line-gold)",
            borderRadius: "999px",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ade80" }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold-2)" }}>{eq.badge}</span>
          </div>
        </div>

        {/* Copy */}
        <div className={`lg:col-span-5 p-8 md:p-12 flex flex-col justify-center ${isEven ? "" : "lg:order-1"}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.42em] mb-4" style={{ color: "var(--gold)" }}>
            Unit · {String(index + 1).padStart(3, "0")} · {eq.year}
          </div>
          <h3 className="font-display" style={{ fontSize: "clamp(1.8rem, 2.6vw, 2.6rem)", letterSpacing: "-0.02em", lineHeight: 1.05 }}>
            {eq.name}
          </h3>
          <div className="mt-3 font-display-italic" style={{ fontSize: "clamp(1.1rem, 1.4vw, 1.4rem)", color: "var(--gold-2)" }}>
            {eq.tagline}
          </div>
          <p className="mt-6 font-body" style={{ color: "var(--text-2)", fontSize: 15, lineHeight: 1.7 }}>
            {eq.body}
          </p>

          {/* Spec grid */}
          <div className="mt-8 grid grid-cols-2 gap-3">
            {eq.specs.map((s, k) => (
              <div key={k} className="p-3.5" style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "12px",
              }}>
                <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                  <s.Icon size={11} style={{ color: "var(--gold)" }} />
                  {s.label}
                </div>
                <div className="mt-1.5 font-display" style={{ fontSize: 20, color: "var(--text)", letterSpacing: "-0.02em" }}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
